/**
 * Faction ops II (website roadmap S20): your faction's roster and its applications — TornStats' roster, Torn Intel's
 * members overview and applications.
 *
 * - Roster: Torn's member list read with your own key (position, days in faction, level, last action, status) plus what
 *   Torn's v2 list adds when a member's Discord-bot key reads it (revive setting, in an OC, on a wall, early discharge —
 *   "what your key reads of your faction", for your faction, an hour in memory: in the terms since version 5), Omni-Torn's
 *   estimates from the player database (estimates only, as the war room shows them) and each member's 30-day activity
 *   once the activity tracker (S22) serves it (read leniently: missing = not shown).
 * - Applications: Torn's /faction/applications needs the faction's "API access". Read only with a Discord-bot key whose
 *   owner agreed to FACTION_APPS_TERMS and is still in the faction; kept FACTION_APPS_KEEP_DAYS on the server for that
 *   faction and shown to its officers only (leader, co-leader, members whose key has the faction's API access). The
 *   battle stats an applicant chose to show the faction in Torn's application are never written to disk — an hour in
 *   memory, officers only — and never go to the player database.
 *
 * The Torn shapes (v2): /faction/members { members: [{ id, name, position, level, days_in_faction, is_revivable,
 * is_on_wall, is_in_oc, has_early_discharge, last_action: { status, timestamp, relative }, status: { description,
 * state, color, until }, revive_setting }] }; /faction/applications { applications: [{ id, user: { id, name, level,
 * stats: { strength, speed, dexterity, defense } | null }, message, valid_until, status }] } (status: active, accepted,
 * declined, withdrawn).
 */

/** The terms version that lets a member's bot key read the faction's applications, kept for its officers. */
export const FACTION_APPS_TERMS = 10;
/** How long applications are kept (from FACTION_APPS_TERMS). */
export const FACTION_APPS_KEEP_DAYS = 90;

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x.trim()) ? Number(x) : null);
const str = (x: unknown): string => (typeof x === 'string' ? x.trim() : '');
const bool = (x: unknown): boolean | null => (typeof x === 'boolean' ? x : null);
const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : obj(x) ? Object.entries(x as Obj).map(([k, v]) => (obj(v) ? { id: Number(k), ...(v as Obj) } : v)) : []);

// ---------------------------------------------------------------- roster

/** What Torn's v2 member list adds to the website's own-key list (from a member's bot key). */
export interface RosterExtra {
  id: number;
  /** "Everyone", "Friends & faction", "No one"… as Torn says it; '' when not given. */
  reviveSetting: string;
  revivable: boolean | null;
  inOc: boolean | null;
  onWall: boolean | null;
  earlyDischarge: boolean | null;
  daysInFaction: number | null;
  position: string;
}

/** GET /api/faction/ops/roster */
export interface FactionRosterAnswer {
  members: RosterExtra[];
  /** When it was read from Torn (ms), or null. */
  readAt: number | null;
  problem: string | null;
}

/** Torn's v2 /faction/members → the extras (members without an id are left out). */
export function readRosterExtras(body: unknown): RosterExtra[] {
  const out: RosterExtra[] = [];
  for (const raw of list(obj(body)?.members)) {
    const m = obj(raw);
    const id = num(m?.id);
    if (!m || !id || id <= 0) continue;
    out.push({
      id,
      reviveSetting: str(m.revive_setting),
      revivable: bool(m.is_revivable),
      inOc: bool(m.is_in_oc),
      onWall: bool(m.is_on_wall),
      earlyDischarge: bool(m.has_early_discharge),
      daysInFaction: num(m.days_in_faction),
      position: str(m.position),
    });
  }
  return out;
}

/** One member's 30-day activity as the activity tracker (S22) gives it. */
export interface MemberActivityShare {
  id: number;
  /** 0–1: the share of the covered time they were seen online or idle; null when not covered. */
  ratio: number | null;
  /** Hours seen active in the span. */
  activeHours: number | null;
}

/**
 * The activity tracker's members answer (S22, `GET /api/faction/activity/members?days=30`), read leniently: a member is
 * `{ id, ratio? (0–1), activeHours? }` (a `percent` 0–100 is taken too). Anything else: none.
 */
export function readMembersActivity(body: unknown): Map<number, MemberActivityShare> {
  const out = new Map<number, MemberActivityShare>();
  for (const raw of list(obj(body)?.members)) {
    const m = obj(raw);
    const id = num(m?.id);
    if (!m || !id || id <= 0) continue;
    let ratio = num(m.ratio);
    if (ratio === null && num(m.percent) !== null) ratio = num(m.percent)! / 100;
    if (ratio !== null) ratio = Math.max(0, Math.min(1, ratio));
    const hours = num(m.activeHours ?? m.active_hours ?? m.hours);
    if (ratio === null && hours === null) continue;
    out.set(id, { id, ratio, activeHours: hours !== null && hours >= 0 ? hours : null });
  }
  return out;
}

/** Torn's positions, most senior first (anything else — a faction's own names — after these, alphabetically). */
export function positionRank(position: string): number {
  const p = position.trim().toLowerCase();
  if (p === 'leader') return 0;
  if (p === 'co-leader' || p === 'co leader') return 1;
  if (p === 'recruit') return 9;
  return 5;
}

/** Leader, co-leader (by name) — the officers Torn's member list can show. */
export const isLeadership = (position: string | null | undefined): boolean => !!position && positionRank(position) <= 1;

/** The member's row as the website's Roster shows it (own-key list + bot extras + estimate + activity). */
export interface RosterRow {
  id: number;
  name: string;
  position: string;
  level: number;
  days: number;
  lastAction: string;
  /** ms, or null. */
  lastActionAt: number | null;
  online: string;
  state: string;
  estimate: { total: number; source: string; at: number } | null;
  activity: MemberActivityShare | null;
  extra: RosterExtra | null;
}

/** Joins the parts by member id (Torn's own-key list decides who's in it). */
export function rosterRows(
  members: ReadonlyArray<{ id: number; name: string; position: string; level: number; days: number; lastAction: string; lastActionAt: number | null; online: string; state: string }>,
  extras: readonly RosterExtra[],
  estimates: ReadonlyArray<{ id: number; total: number; source: string; at: number }>,
  activity: ReadonlyMap<number, MemberActivityShare>,
): RosterRow[] {
  const ex = new Map(extras.map((x) => [x.id, x]));
  const est = new Map(estimates.map((e) => [e.id, e]));
  return members.map((m) => {
    const x = ex.get(m.id) ?? null;
    return { ...m, position: m.position || x?.position || '', days: m.days || x?.daysInFaction || 0, estimate: est.get(m.id) ?? null, activity: activity.get(m.id) ?? null, extra: x };
  });
}

/** A roster's headline numbers. */
export function rosterSummary(rows: readonly RosterRow[], nowMs: number): { members: number; avgDays: number; activeDay: number; estimated: number; inOc: number; revivable: number } {
  const n = rows.length;
  return {
    members: n,
    avgDays: n ? Math.round(rows.reduce((a, r) => a + r.days, 0) / n) : 0,
    activeDay: rows.filter((r) => r.lastActionAt !== null && nowMs - r.lastActionAt < 86_400_000).length,
    estimated: rows.filter((r) => r.estimate).length,
    inOc: rows.filter((r) => r.extra?.inOc).length,
    revivable: rows.filter((r) => r.extra?.revivable).length,
  };
}

// ---------------------------------------------------------------- applications

export type ApplicationStatus = 'active' | 'accepted' | 'declined' | 'withdrawn' | 'other';

/** Battle stats an applicant chose to show the faction in Torn's application (never kept on disk). */
export interface ApplicantStats {
  strength: number;
  speed: number;
  dexterity: number;
  defense: number;
  total: number;
}

export interface FactionApplication {
  /** Torn's application id. */
  id: number;
  user: { id: number; name: string; level: number };
  message: string;
  /** Seconds. */
  validUntil: number;
  status: ApplicationStatus;
  /** When Omni-Torn first saw it (seconds). */
  firstSeen: number;
  /** Only from the latest read (an hour in memory), only when the applicant showed them. */
  stats?: ApplicantStats | null;
}

/** GET /api/faction/ops/applications */
export interface FactionApplicationsAnswer {
  /** Are you one of the faction's officers (leader, co-leader, members whose key has the faction's API access)? */
  officer: boolean;
  /** Can Omni-Torn read them (a bot key with the faction's API access whose owner agreed to FACTION_APPS_TERMS)? */
  readable: boolean;
  terms: number;
  /** Days kept; 0 = no end date (terms KEEP_ALL_TERMS, "No age limits"). */
  keepDays: number;
  readAt: number | null;
  applications: FactionApplication[];
  /** Omni-Torn's estimate of each applicant (player database). */
  estimates: Array<{ id: number; total: number; source: string; at: number }>;
  problem: string | null;
}

const STATUSES: readonly ApplicationStatus[] = ['active', 'accepted', 'declined', 'withdrawn'];

/** Torn's v2 /faction/applications → applications (bad entries left out). `seenAt` (seconds) is the read's time. */
export function readApplications(body: unknown, seenAt: number): FactionApplication[] {
  const out: FactionApplication[] = [];
  for (const raw of list(obj(body)?.applications)) {
    const a = obj(raw);
    const u = obj(a?.user);
    const id = num(a?.id);
    const uid = num(u?.id);
    if (!a || !u || !id || !uid || uid <= 0) continue;
    const s = obj(u.stats);
    const four = s ? (['strength', 'speed', 'dexterity', 'defense'] as const).map((k) => num(s[k])) : null;
    const stats = four && four.every((v) => v !== null && v >= 0) ? { strength: four[0]!, speed: four[1]!, dexterity: four[2]!, defense: four[3]!, total: four.reduce<number>((x, v) => x + v!, 0) } : null;
    const status = str(a.status).toLowerCase() as ApplicationStatus;
    out.push({
      id,
      user: { id: uid, name: str(u.name) || `#${uid}`, level: num(u.level) ?? 0 },
      message: str(a.message).slice(0, 2000),
      validUntil: num(a.valid_until) ?? 0,
      status: STATUSES.includes(status) ? status : 'other',
      firstSeen: seenAt,
      stats,
    });
  }
  return out;
}

/** What goes on disk: everything but the stats. */
export const applicationToKeep = (a: FactionApplication): FactionApplication => {
  const { stats: _stats, ...rest } = a;
  return rest;
};

/** Waiting ones first (soonest to run out first), then the rest newest first. */
export function sortApplications(apps: readonly FactionApplication[]): FactionApplication[] {
  return [...apps].sort((a, b) => Number(b.status === 'active') - Number(a.status === 'active') || (a.status === 'active' ? a.validUntil - b.validUntil : b.firstSeen - a.firstSeen || b.id - a.id));
}
